import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Store } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useDash, money, clock } from "../lib/dash";
import { atLeast } from "../components/AdminAreaGate";
import { Button } from "../components/ui/button";
import { AccountMenu } from "../components/AccountMenu";
import { TileState, Stat, Chip } from "../components/admin/Tile";
import { revenueText, orderNum, ducksText } from "../components/admin/accounts/PartnerOrders";

const PERIODS = [[30, "30 days"], [90, "90 days"], [365, "1 year"], [0, "All time"]];
const SOURCE = { "own duck": ["Your duck", "green"], "linked by admin": ["Linked by Duckify", "blue"] };

const Periods = ({ days, onDays }) => (
  <div data-testid="partner-periods" className="flex rounded-full border border-border bg-muted/40 p-1">
    {PERIODS.map(([d, label]) => (
      <button key={d} type="button" data-testid={`partner-period-${d}`} aria-pressed={days === d} onClick={() => onDays(d)}
        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${days === d ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
        {label}
      </button>
    ))}
  </div>
);

const OrderRow = ({ o }) => (
  <tr data-testid={`partner-row-${orderNum(o.order_id)}`} className="border-b border-border/60 last:border-0">
    <td className="py-2 pr-3 font-semibold">{o.order_name}</td>
    <td className="py-2 pr-3 text-muted-foreground">{clock(o.created_at)}</td>
    <td className="py-2 pr-3 capitalize">{o.status}</td>
    <td className="max-w-[320px] truncate py-2 pr-3">{ducksText(o)}</td>
    <td className="py-2 pr-3 text-right font-semibold tabular-nums">{money(o.amount, o.currency)}</td>
    <td className="py-2"><Chip tone={SOURCE[o.source]?.[1]}>{SOURCE[o.source]?.[0] || o.source}</Chip></td>
  </tr>
);

const Orders = ({ days }) => {
  const s = useDash(`/partner/orders?days=${days}`, { poll: false });
  const d = s.data;
  return (
    <TileState state={s}>
      {d && (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat testid="partner-total-orders" label="Orders" value={d.totals.orders} />
            <Stat testid="partner-total-ducks" label="Ducks sold" value={d.totals.ducks} />
            <Stat testid="partner-total-sales" label="Sales" value={revenueText(d.totals.revenue)} />
            <Stat testid="partner-total-cancelled" label="Cancelled" value={d.totals.cancelled} />
          </div>
          <div className="dash-scroll min-h-0 flex-1 overflow-auto rounded-2xl border border-border bg-card px-4 py-2">
            {d.orders.length ? (
              <table data-testid="partner-orders-table" className="w-full text-left text-sm">
                <thead className="font-mono2 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr><th className="py-2 pr-3">Order</th><th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Ducks</th><th className="py-2 pr-3 text-right">Amount</th><th className="py-2">Why it's yours</th></tr>
                </thead>
                <tbody>{d.orders.map((o) => <OrderRow key={o.order_id} o={o} />)}</tbody>
              </table>
            ) : <p data-testid="partner-no-orders" className="py-10 text-center text-sm text-muted-foreground">No orders in this period yet.</p>}
          </div>
        </div>
      )}
    </TileState>
  );
};

// /partner: the signed-in partner's own orders and sales, nothing else (never a customer's name, address or contact).
export default function PartnerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  return (
    <div data-testid="partner-page" className="App flex min-h-screen flex-col bg-dots lg:h-screen lg:overflow-hidden">
      <main className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col px-4 py-6 sm:px-8">
        <div className="flex items-center justify-between gap-3">
          <Button variant="ghost" className="-ml-3 w-fit gap-2 rounded-full" data-testid="partner-back-btn" onClick={() => navigate("/")}>
            <ArrowLeft className="h-4 w-4" /> Back to the studio
          </Button>
          <AccountMenu testid="partner-account-menu" />
        </div>
        <div className="mb-5 mt-2 flex flex-wrap items-end justify-between gap-3">
          <div>
            <span className="flex items-center gap-1.5 font-mono2 text-xs font-semibold uppercase tracking-widest text-accent"><Store className="h-3.5 w-3.5" /> Partner</span>
            <h1 className="mt-1 font-heading text-4xl font-extrabold tracking-tight">Your orders &amp; sales</h1>
          </div>
          {atLeast(user, "partner") && <Periods days={days} onDays={setDays} />}
        </div>
        {atLeast(user, "partner") ? (
          <>
            <p data-testid="partner-privacy-note" className="mb-4 flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" /> Orders with your own ducks, plus any Duckify linked to you. Customer names, addresses, emails and phones are never shown.
            </p>
            <Orders days={days} />
          </>
        ) : (
          <p data-testid="partner-no-role" className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
            This page is for Duckify partners. Your account isn't a partner account.
          </p>
        )}
      </main>
    </div>
  );
}
